import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useRouteMatch, useLocation } from 'react-router-dom';
// Hooks
import useMenu from 'app/hooks/useMenu';
import useVisibleTabs from 'app/hooks/useVisibleTabs';
import useAccountStatus from 'app/hooks/useAccountStatus';
// Types
import UserPermissions from 'app/types/Permissions';
import UserRoles from 'app/types/UserRoles';
import CaseStatuses from 'app/types/CaseStatuses';
// Models
import { RootState } from 'app/store';
import { IMyUser } from 'app/models/User';
import IInsuranceCase from 'app/models/Case';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectMyUser, selectMyUserHasPermission } from 'app/store/Users/Users.selectors';
import { selectInsuranceCase } from 'app/store/Cases/Cases.selectors';
// Mui
import { Theme, Tabs, Tooltip, Box } from '@mui/material';
// Icons
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
// Components
import { IconButton, Tab } from 'app/components/Mui';
import { selectAIChatEnabled, selectInvoicingEnabled } from 'app/store/Accounts/Accounts.selectors';
import { selectTrialMode } from 'app/store/AppUI/AppUI.selectors';
import { selectHasSubscriptionWithAdditionalItems } from 'app/store/BillingSubscriptions/BillingSubscriptions.selectors';
// i18next
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';

interface ILink {
  value: string,
  to: string,
  visible?: boolean,
  disabled?: boolean,
  label: string,
  hint?: string,
}

const CaseDetailsNavs:FC = () => {
  const { t } = useTranslation();

  const { url } = useRouteMatch();
  const { pathname } = useLocation<{ pathname:string }>();
  // State
  const trialMode = useAppSelector(selectTrialMode);
  const invoicingEnabled = useAppSelector(selectInvoicingEnabled);
  const aiChatEnabled = useAppSelector(selectAIChatEnabled);
  const myUser:IMyUser | null = useAppSelector(selectMyUser);
  const myUserInvoiceViewEnabled = useAppSelector((state:RootState) => selectMyUserHasPermission(state, {
    permission: UserPermissions.InvoiceView
  }));
  const insuranceCase:IInsuranceCase | null = useAppSelector(selectInsuranceCase);
  const hasSubscriptionWithAdditionalItems = useAppSelector(selectHasSubscriptionWithAdditionalItems);

  const lastDocument = localStorage.getItem('shimlaw_ep:last_document') || `{}`;
  const parsed = JSON.parse(lastDocument);
  const lastDocumentId = insuranceCase?.id ? parsed[insuranceCase.id] : null;

  const [ value, setValue ] = useState<string | boolean>('documents');

  const { isSuspended } = useAccountStatus();

  const links:ILink[] = useMemo(() => {
    const isCaseStatusOpen = insuranceCase?.status === CaseStatuses.Open;
    const isCaseStatusArchive = insuranceCase?.status === CaseStatuses.Archived;
    const isCaseProcessingStatusReady = insuranceCase?.processing?.status === 'ready';

    const isMyUserRoleAdmin = myUser?.role === UserRoles.Admin;
    const isMyUserRoleStaff = myUser?.role === UserRoles.Staff;

    const isDuplicatesDisabled = !(isCaseProcessingStatusReady && (isCaseStatusOpen || isCaseStatusArchive));
    const isInvoicesVisible = !trialMode && ((isMyUserRoleStaff && myUserInvoiceViewEnabled) || invoicingEnabled);
    const isAIChatVisible = !trialMode && aiChatEnabled;

    const documentsExportsEnabled = (isCaseStatusOpen || isCaseStatusArchive);

    return [
      {
        value: 'documents',
        to: `documents${lastDocumentId ? `/${lastDocumentId}` : ''}`,
        visible: !trialMode,
        label: t('components.caseTabs.files')
      },
      {
        value: 'workspace',
        to: 'workspace',
        label: t('components.caseTabs.workspace')
      },
      {
        value: 'calendar',
        to: 'calendar',
        label: t('components.caseTabs.calendar')
      },
      {
        value: 'reports',
        to: 'reports',
        visible: !trialMode,
        label: t('components.caseTabs.reports')
      },
      {
        value: 'invoices',
        to: 'invoices',
        visible: isInvoicesVisible,
        label: t('components.caseTabs.invoices')
      },
      {
        value: 'templates',
        to: 'templates',
        visible: !trialMode,
        label: t('components.caseTabs.templates')
      },
      {
        value: 'workflows',
        to: 'workflows',
        visible: !trialMode && (isMyUserRoleAdmin || isMyUserRoleStaff),
        disabled: isSuspended,
        label: t('components.caseTabs.workflows')
      },
      {
        value: 'ai-chat',
        to: 'ai-chat',
        visible: isAIChatVisible,
        disabled: isSuspended,
        label: t('components.caseTabs.aiChat')
      },
      {
        value: 'search',
        to: 'search',
        visible: !trialMode,
        label: t('components.caseTabs.search')
      },
      {
        value: 'episodes',
        to: 'episodes',
        visible: !trialMode,
        label: t('components.caseTabs.episodes')
      },
      {
        value: 'duplicates',
        to: 'duplicates',
        visible: !trialMode,
        disabled: isDuplicatesDisabled,
        label: t('components.caseTabs.duplicates'),
        hint: isDuplicatesDisabled ? t('components.caseTabs.duplicatesDisabledHint') : ''
      },
      {
        value: 'messages',
        to: 'messages',
        visible: !trialMode,
        label: t('components.caseTabs.messages')
      },
      {
        value: 'case-shares',
        to: 'case-shares',
        visible: !trialMode && isMyUserRoleAdmin,
        label: t('components.caseTabs.caseShares')
      },
      {
        value: 'documents-exports',
        to: 'documents-exports',
        visible: !trialMode && documentsExportsEnabled && isMyUserRoleAdmin,
        label: t('components.caseTabs.documentsExports'),
      },
      {
        value: 'audit-log',
        to: 'audit-log',
        visible: !trialMode,
        label: t('components.caseTabs.auditLog')
      },
      {
        value: 'orders',
        to: 'orders',
        visible: !trialMode && hasSubscriptionWithAdditionalItems,
        label: t('components.caseTabs.orders'),
      },
    ];
    // eslint-disable-next-line
  }, [i18n.language, trialMode, lastDocumentId, insuranceCase, myUser, invoicingEnabled, aiChatEnabled, isSuspended]);

  const { Menu, MenuItem, openMenu } = useMenu();

  const {
    containerRef,
    visibleTabs,
    overflowTabs,
  } = useVisibleTabs(links);

  useEffect(() => {
    const activePath = pathname.split('/')[4];

    const foundedLink = visibleTabs.find((link:any) => activePath === link.value);

    setValue(foundedLink?.value || false);
    // eslint-disable-next-line
  }, [pathname, visibleTabs]);

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }} ref={containerRef}>
      <Tabs
        value={value}
        TabScrollButtonProps={{
          sx: {
            '&.MuiTabs-scrollButtons': {
              '&:hover': {
                bgcolor: (theme:Theme) => theme.palette.primary.light
              },
              '&.Mui-disabled': {
                opacity: 0.25
              }
            }
          }
        }}
      >
        {visibleTabs.map((link, index) => {
          if ( typeof link.visible !== 'undefined' && !link.visible ) return null;
          return (
            <Tab
              key={`nav item ${link.to}`}
              name={link.to}
              sx={{
                minWidth: 'initial',
                minHeight: { xs: 56, sm: 63 },
                fontSize: 16,
                px: 3,
                textTransform: 'capitalize',
                pointerEvents: 'auto !important',
                '&:hover': {
                  background: link.disabled ? 'none' : 'auto',
                  color: link.disabled ? 'rgba(0, 0, 0, 0.38)' : 'auto',
                }
              }}
              component={!link.disabled ? Link : undefined}
              to={`${url}/${link.to}`}
              value={link.value}
              label={(
                <Tooltip title={link.hint}>
                  <span>{link.label}</span>
                </Tooltip>
              )}
              disabled={typeof link.disabled !== 'undefined' ? link.disabled : false}
            />
          )
        })}
      </Tabs>
      {overflowTabs.length > 0 ? (
        <Fragment>
          <IconButton
            name="context-menu"
            onClick={openMenu}
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu>
            {overflowTabs.map(link => {
              if ( typeof link.visible !== 'undefined' && !link.visible ) return null;
              return (
                <MenuItem
                  key={`nav item ${link.to}`}
                  name={link.to}
                  component={!link.disabled ? Link : undefined}
                  to={`${url}/${link.to}`}
                  disabled={typeof link.disabled !== 'undefined' ? link.disabled : false}
                  selected={pathname.split('/')[4] === link.value}
                >
                  {link.label}
                </MenuItem>
              )
            })}
          </Menu>
        </Fragment>
      ) : null}
    </Box>
  )
}

export default CaseDetailsNavs;
